import React, { useState, useEffect, useRef } from 'react';
import { FARMING_TYPES, FUNDING_TYPES, FUNDING_AIMS, INTERESTS, LAND_TYPES, STATUS_OPTIONS, PROTECTED_LAND_TYPES, COMPETITIVE_STATUS, COUNTRY_CHOICES, COUNTIES_BY_COUNTRY } from '../../utils/constants';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { usePostHog } from '@posthog/react';

const FundingFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
  onClearSearch,
  resultsCount = 0,
  currentResultsLength = 0,
  groupCount = 0,
  hasActiveSearch = false,
  searchQuery = '',
  loading = false,
  searchBarComponent = null,
  sheetMode = false,
  locationFilter = { country: [], counties: [] },
  onLocationFilterChange,
  locationPopupOpen = false,
  onLocationPopupToggle,
  isAuthenticated = true,
  onRequireAuth,
  onShowResults,
}) => {
  const posthog = usePostHog();
  const [isOpen, setIsOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [isStackHovered, setIsStackHovered] = useState(false);
  const [isFilterInfoHovered, setIsFilterInfoHovered] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 220 });
  const gridRef = useRef(null);
  const buttonRefs = useRef({});
  const [fundCollections, setFundCollections] = useState([]);
  const [funders, setFunders] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [availableValues, setAvailableValues] = useState(null);

  // Stable serialised values for location so the effect doesn't re-run on every render
  const locationCountry = (locationFilter.country || []).join(',');
  const locationCounties = (locationFilter.counties || []).join(',');

  // Use a ref to read current filters inside the effect without adding them as dependencies
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const fetchAvailableFilters = async () => {
      try {
        setLoadingFilters(true);
        const params = new URLSearchParams();
        if (locationCountry) {
          params.set('location', locationCountry.split(',').map(c => c.toLowerCase().replace(/ /g, '_')).join(','));
        }
        if (locationCounties) {
          params.set('counties', locationCounties);
        }
        const response = await api.get(`/funding-schemes/available-filters/?${params.toString()}`);
        const data = response.data;

        const availableFunderIds = new Set(data.funders.map(f => f.value));
        const availableCollectionIds = new Set(data.fund_collection.map(c => c.value));

        // Keep selected-but-unavailable funders visible with a warning
        const selectedFunderIds = filtersRef.current.funders || [];
        const newFunders = data.funders.map(f => ({ id: f.value, name: f.label }));
        setFunders(prev => {
          const orphaned = selectedFunderIds
            .filter(id => !availableFunderIds.has(id))
            .map(id => {
              const existing = prev.find(f => f.id === id);
              return { id, name: existing?.name || 'Not specified', unavailable: true };
            });
          return [...newFunders, ...orphaned];
        });

        // Keep selected-but-unavailable collections visible with a warning
        const selectedCollectionIds = filtersRef.current.fund_collection || [];
        const newCollections = data.fund_collection.map(c => ({ id: c.value, name: c.label, funder: c.funder }));
        setFundCollections(prev => {
          const orphaned = selectedCollectionIds
            .filter(id => !availableCollectionIds.has(id))
            .map(id => {
              const existing = prev.find(c => c.id === id);
              return { id, name: existing?.name || 'Not specified', funder: existing?.funder || 'Other', unavailable: true };
            });
          return [...newCollections, ...orphaned];
        });

        setAvailableValues(data);
      } catch (error) {
        console.error('Error fetching available filters:', error);
        setFunders([]);
        setFundCollections([]);
        setAvailableValues(null);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchAvailableFilters();
  }, [locationCountry, locationCounties]);

  const sortOptions = [
    { value: '', label: 'Default' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'status_priority', label: 'Status' },
    { value: 'closes', label: 'Deadline (Earliest)' },
    { value: '-closes', label: 'Deadline (Latest)' },
    { value: '-created_at', label: 'Recently Added' },
    { value: '-avg_payment_rate', label: 'Payment Rate (Highest)' },
    { value: 'avg_payment_rate', label: 'Payment Rate (Lowest)' }
  ];

  const getSortOptions = () => {
    if (hasActiveSearch) {
      return [
        {value: '-_search_score', label: 'Relevance'},
        ...sortOptions
      ];
    }
    return sortOptions;
  };

  const getCurrentSortLabel = () => {
    if (!filters.ordering) return 'Sort';
    const currentSort = getSortOptions().find(option => option.value === filters.ordering);
    return currentSort ? currentSort.label : 'Sort';
  };

  const handleSortChange = (value) => {
    posthog.capture('funding_filtered', { filter_type: 'sort', sort_value: value });
    onFilterChange({ ordering: value });
    setIsSortOpen(false);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const groupOptions = [
    { value: 'fund_collection', label: 'Fund Collection' },
    { value: 'funder', label: 'Funder' },
    { value: 'funding_type', label: 'Funding Type' },
    { value: 'aims', label: 'Aims' },
    { value: 'interests', label: 'Interests' }
  ];

  const getGroupLabel = (value) => {
    return groupOptions.find(opt => opt.value === value)?.label || value;
  };

  const handleProtectedLandChange = (landType, checked) => {
    posthog.capture('funding_filtered', { filter_type: 'protected_land', filter_value: landType, filter_action: checked ? 'add' : 'remove' });
    const current = filters.protected_land || [];
    const updated = checked
      ? [...current, landType]
      : current.filter(item => item !== landType);
    onFilterChange({ protected_land: updated });
  };

  const handleArrayFilterChange = (filterKey, value, checked) => {
    posthog.capture('funding_filtered', { filter_type: filterKey, filter_value: value, filter_action: checked ? 'add' : 'remove' });
    const current = filters[filterKey] || [];
    const updated = checked
      ? [...current, value]
      : current.filter(item => item !== value);
    onFilterChange({ [filterKey]: updated });
  };

  const handleThresholdChange = (value) => {
    onFilterChange({ protected_land_threshold: value });
  };

  const sortByLabel = (opts) => [...opts].sort((a, b) => a.label.localeCompare(b.label));

  // Mark options as unavailable rather than removing them, so selected-but-unavailable filters stay visible
  const filterByAvailable = (options, availableList, filterKey) => {
    if (!availableValues || !availableList) return options;
    const availableSet = new Set(availableList.map(v => typeof v === 'string' ? v.toLowerCase() : v));
    const selected = filters[filterKey] || [];
    return options
      .filter(opt => availableSet.has(typeof opt.value === 'string' ? opt.value.toLowerCase() : opt.value) || selected.includes(opt.value))
      .map(opt => ({
        ...opt,
        unavailable: !availableSet.has(typeof opt.value === 'string' ? opt.value.toLowerCase() : opt.value),
      }));
  };

  const filterConfigs = [
    { key: 'funders',         label: 'Funder',             loading: loadingFilters, options: sortByLabel(funders.map(f => ({ value: f.id, label: f.name, unavailable: f.unavailable }))) },
    { key: 'fund_collection', label: 'Fund Collection',    loading: loadingFilters, grouped: true, options: fundCollections.map(c => ({ value: c.id, label: c.name, group: c.funder || 'Other', unavailable: c.unavailable })) },
    { key: 'farming_type',    label: 'Farm Type',          options: sortByLabel(filterByAvailable(FARMING_TYPES, availableValues?.farming_type, 'farming_type')) },
    { key: 'competitive',     label: 'Competitive Status', options: sortByLabel(filterByAvailable(COMPETITIVE_STATUS, availableValues?.competitive, 'competitive')) },
    { key: 'status',          label: 'Status',             options: sortByLabel(filterByAvailable(STATUS_OPTIONS, availableValues?.status, 'status')) },
    { key: 'funding_type',    label: 'Funding Type',       options: sortByLabel(filterByAvailable(FUNDING_TYPES, availableValues?.funding_type, 'funding_type')) },
    { key: 'land_types',      label: 'Land Types and Use', options: sortByLabel(filterByAvailable(LAND_TYPES, availableValues?.land_types, 'land_types')) },
    { key: 'aim',             label: 'Aims',               options: sortByLabel(filterByAvailable(FUNDING_AIMS, availableValues?.aims, 'aim')) },
    { key: 'interests',       label: 'Interests',          options: sortByLabel(filterByAvailable(INTERESTS, availableValues?.interests, 'interests')) },
  ];

  // Check if a filter value is unavailable for the current location
  const isFilterValueUnavailable = (filterKey, value) => {
    const config = filterConfigs.find(c => c.key === filterKey);
    if (!config) return false;
    const option = config.options.find(opt => opt.value === value);
    return option?.unavailable || false;
  };

  const handleFilterButtonClick = (key) => {
    if (activeDropdown === key) {
      setActiveDropdown(null);
      return;
    }
    const buttonEl = buttonRefs.current[key];
    if (!buttonEl || !gridRef.current) return;
    const buttonRect = buttonEl.getBoundingClientRect();
    const gridRect = gridRef.current.getBoundingClientRect();
    const config = filterConfigs.find(c => c.key === key);
    const isLargeList = config && config.options.length > 5;
    const viewportWidth = window.innerWidth;
    const margin = 8; // min gap from screen edge
    let left, dropWidth;
    if (key === 'counties') {
      dropWidth = 360;
      const centeredLeft = buttonRect.left + buttonRect.width / 2 - dropWidth / 2;
      left = Math.max(gridRect.left, Math.min(centeredLeft, gridRect.right - dropWidth));
    } else if (isLargeList) {
      dropWidth = 480;
      const centeredLeft = gridRect.left + gridRect.width / 2 - dropWidth / 2;
      left = Math.max(gridRect.left, Math.min(centeredLeft, gridRect.right - dropWidth));
    } else {
      dropWidth = Math.max(buttonRect.width, 220);
      left = buttonRect.left;
    }
    // Clamp width and left to viewport so dropdown never overflows on mobile
    dropWidth = Math.min(dropWidth, viewportWidth - margin * 2);
    left = Math.max(margin, Math.min(left, viewportWidth - dropWidth - margin));
    setDropdownPos({ top: buttonRect.bottom + 4, left, width: dropWidth });
    setActiveDropdown(key);
  };

  // Close dropdown if window is resized (positions would be stale)
  useEffect(() => {
    if (!activeDropdown) return;
    const handleResize = () => setActiveDropdown(null);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeDropdown]);

  const hasActiveFilters = Object.entries(filters).some(([key, value]) =>
    key !== 'group_by' && key !== 'ordering' && key !== 'compatible_with' && (Array.isArray(value) ? value.length > 0 : value)
  );

  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'group_by' || key === 'ordering' || key === 'compatible_with') return count;
    if (Array.isArray(value)) return count + value.length;
    if (value) return count + 1;
    return count;
  }, 0);

  return (
    <>
      {/* Controls toolbar — hidden in sheetMode (sheet provides its own header) */}
      {!sheetMode && (
      <>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {/* Filters Button — solid purple, on the left */}
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
            style={{
              backgroundColor: '#615DEC',
              borderColor: '#615DEC',
              color: '#fff',
            }}
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full" style={{ backgroundColor: '#ffffff', color: '#615DEC' }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Location filter button + popup */}
          <div className="relative">
            <button
              type="button"
              onClick={() => onLocationPopupToggle(!locationPopupOpen)}
              className="inline-flex items-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
              style={{
                backgroundColor: locationFilter.country.length > 0 ? '#f0fdf4' : '#fff',
                borderColor: locationFilter.country.length > 0 ? '#16a34a' : '#d1d5db',
                color: locationFilter.country.length > 0 ? '#166534' : '#374151',
              }}
            >
              <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {locationFilter.country.length > 0 ? (
                <span>
                  {locationFilter.counties.length > 0 ? (
                    locationFilter.country.map((c, i) => {
                      const countryCounties = COUNTIES_BY_COUNTRY.find(entry => entry.country === c);
                      const selectedCounties = countryCounties
                        ? countryCounties.counties.filter(county => locationFilter.counties.includes(county))
                        : [];
                      return (
                        <span key={c}>
                          {i > 0 && <span className="mx-1">|</span>}
                          {c}
                          {selectedCounties.length > 0 && (
                            <span className="font-normal opacity-75"> ({selectedCounties.length})</span>
                          )}
                        </span>
                      );
                    })
                  ) : (
                    locationFilter.country.join(', ')
                  )}
                </span>
              ) : (
                'Location'
              )}
              <svg className={`h-4 w-4 ml-2 transition-transform ${locationPopupOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {locationPopupOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => onLocationPopupToggle(false)} />
                <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80 sm:w-96 max-h-[70vh] overflow-y-auto">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Filter by location</h3>

                    {/* Country selection */}
                    <div className="mb-3">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Country</span>
                      <div className="mt-2 space-y-1">
                        {COUNTRY_CHOICES.map(({ value, label }) => (
                          <label key={value} className="flex items-center cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded">
                            <input
                              type="checkbox"
                              checked={locationFilter.country.includes(value)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                posthog.capture('funding_filtered', { filter_type: 'location_country', filter_value: value, filter_action: checked ? 'add' : 'remove' });
                                onLocationFilterChange(prev => {
                                  const newCountry = checked
                                    ? [...prev.country, value]
                                    : prev.country.filter(c => c !== value);
                                  const validCounties = checked
                                    ? prev.counties
                                    : prev.counties.filter(county => {
                                        const entry = COUNTIES_BY_COUNTRY.find(cb => cb.country === value);
                                        return !entry || !entry.counties.includes(county);
                                      });
                                  return { country: newCountry, counties: validCounties };
                                });
                              }}
                              className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded flex-shrink-0"
                            />
                            <span className="ml-2 text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Hint — nudges user to select a country for county filtering */}
                    {locationFilter.country.length === 0 && (
                      <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-400">
                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                        </svg>
                        <span>Select a country to filter by county</span>
                      </div>
                    )}

                    {/* County selection — shown per selected country */}
                    {locationFilter.country.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Counties</span>
                        {locationFilter.country.map(country => {
                          const entry = COUNTIES_BY_COUNTRY.find(cb => cb.country === country);
                          if (!entry) return null;
                          return (
                            <div key={country} className="mt-2">
                              <div className="px-2 pb-1 text-xs font-semibold text-gray-400 border-b border-gray-100 mb-1">{country}</div>
                              <div className="grid grid-cols-2 gap-x-1 gap-y-0">
                                {entry.counties.map(county => (
                                  <label key={county} className="flex items-center cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded">
                                    <input
                                      type="checkbox"
                                      checked={locationFilter.counties.includes(county)}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        posthog.capture('funding_filtered', { filter_type: 'location_county', filter_value: county, filter_action: checked ? 'add' : 'remove' });
                                        onLocationFilterChange(prev => ({
                                          ...prev,
                                          counties: checked
                                            ? [...prev.counties, county]
                                            : prev.counties.filter(c => c !== county),
                                        }));
                                      }}
                                      className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded flex-shrink-0"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 leading-tight">{county}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <button
                      type="button"
                      onClick={() => onLocationFilterChange({ country: [], counties: [] })}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onLocationPopupToggle(false);
                        if (typeof onShowResults === 'function') onShowResults();
                      }}
                      className="px-4 py-1.5 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-sm font-medium min-h-[44px] px-2 hover:underline transition-all"
              style={{ color: '#7C7B7B' }}
            >
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-2">
          {loading && <LoadingSpinner size="small" />}

          {/* Stack Icon */}
          {!(filters.compatible_with && filters.compatible_with.length > 0) && (
            <div
              className="relative"
              onMouseEnter={() => setIsStackHovered(true)}
              onMouseLeave={() => setIsStackHovered(false)}
            >
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] rounded-md border-2 transition-colors focus:outline-none text-sm font-semibold"
                style={{
                  borderColor: isStackHovered ? '#615DEC' : '#000',
                  color: isStackHovered ? '#615DEC' : '#000',
                  backgroundColor: isStackHovered ? '#F6F5EC' : '#fff',
                }}
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                <span className="hidden md:inline">Build a Stack</span>
              </button>
              {isStackHovered && (
                <div
                  className="absolute right-0 mt-2 px-4 py-3 rounded-xl shadow-xl text-sm font-medium leading-relaxed z-50"
                  style={{ backgroundColor: '#222222', color: '#ffffff', width: '260px' }}
                >
                  Build a stack: search for opportunities or browse below, then click this icon to stack them.
                  <div
                    className="absolute -top-1.5 right-3 w-3 h-3 rotate-45"
                    style={{ backgroundColor: '#222222' }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Sort Button */}
          <div className="relative">
            <button
              onClick={() => {
                if (!isAuthenticated) { if (typeof onRequireAuth === 'function') onRequireAuth(); return; }
                setIsSortOpen(!isSortOpen);
              }}
              className="inline-flex items-center justify-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
              style={{
                backgroundColor: filters.ordering ? '#000' : '#fff',
                borderColor: '#000',
                color: filters.ordering ? '#fff' : '#000',
                transition: 'background 0.2s, color 0.2s'
              }}
              onMouseEnter={e => {
                if (!filters.ordering) {
                  e.currentTarget.style.backgroundColor = '#000';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={e => {
                if (!filters.ordering) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = '#000';
                }
              }}
            >
              <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
              </svg>
              <span className="hidden md:inline ml-2">{getCurrentSortLabel()}</span>
              <svg className={`h-4 w-4 ml-1 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Sort Options Modal */}
            {isSortOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsSortOpen(false)}
                />
                <div
                  className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-50 border-2"
                  style={{ backgroundColor: '#ffffff', borderColor: '#615DEC' }}
                >
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold" style={{ color: '#7C7B7B', backgroundColor: '#F6F5EC' }}>
                      SORT OPTIONS
                    </div>
                    {getSortOptions().map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all hover:opacity-80 ${
                          (filters.ordering || '') === option.value ? 'font-semibold' : ''
                        }`}
                        style={{
                          backgroundColor: (filters.ordering || '') === option.value ? '#F6F5EC' : 'transparent',
                          color: (filters.ordering || '') === option.value ? '#615DEC' : '#222222'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          {(filters.ordering || '') === option.value && (
                            <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Group Button */}
          <div className="relative">
            <button
              onClick={() => {
                if (!isAuthenticated) { if (typeof onRequireAuth === 'function') onRequireAuth(); return; }
                setIsGroupOpen(!isGroupOpen);
              }}
              className="inline-flex items-center justify-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
              style={{
                backgroundColor: filters.group_by ? '#000' : '#fff',
                borderColor: '#000',
                color: filters.group_by ? '#fff' : '#000',
                transition: 'background 0.2s, color 0.2s'
              }}
              onMouseEnter={e => {
                if (!filters.group_by) {
                  e.currentTarget.style.backgroundColor = '#000';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={e => {
                if (!filters.group_by) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = '#000';
                }
              }}
            >
              <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="hidden md:inline ml-2">
                {filters.group_by ? `Grouped: ${getGroupLabel(filters.group_by)}` : 'Group'}
              </span>
              <svg className={`h-4 w-4 ml-1 transition-transform ${isGroupOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Group Options Modal */}
            {isGroupOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsGroupOpen(false)} />
                <div className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-50 border-2"
                  style={{ backgroundColor: '#ffffff', borderColor: '#615DEC' }}>
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold" style={{ color: '#7C7B7B', backgroundColor: '#F6F5EC' }}>
                      GROUP BY
                    </div>
                    <button
                      onClick={() => { posthog.capture('funding_filtered', { filter_type: 'group_by', filter_value: 'none' }); onFilterChange({ group_by: '' }); setIsGroupOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm font-medium hover:opacity-80"
                      style={{
                        backgroundColor: !filters.group_by ? '#F6F5EC' : 'transparent',
                        color: !filters.group_by ? '#615DEC' : '#222222'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span>None</span>
                        {!filters.group_by && (
                          <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                    {groupOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => { posthog.capture('funding_filtered', { filter_type: 'group_by', filter_value: option.value }); onFilterChange({ group_by: option.value }); setIsGroupOpen(false); }}
                        className="w-full text-left px-4 py-3 text-sm font-medium hover:opacity-80"
                        style={{
                          backgroundColor: filters.group_by === option.value ? '#F6F5EC' : 'transparent',
                          color: filters.group_by === option.value ? '#615DEC' : '#222222'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          {filters.group_by === option.value && (
                            <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      </>
      )}


      {/* Filter content — rendered in desktop modal (isOpen) or inline in bottom sheet (sheetMode) */}
      {(isOpen || sheetMode) && (
        <div className={!sheetMode ? 'fixed inset-0 z-50 overflow-y-auto' : ''}>
          {/* Backdrop — desktop modal only */}
          {!sheetMode && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => { setIsOpen(false); setActiveDropdown(null); }}
            />
          )}

          <div className={!sheetMode ? 'flex min-h-full items-center justify-center p-4' : ''}>
            <div className={!sheetMode ? 'relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col' : ''}>

              {/* Header — desktop modal only */}
              {!sheetMode && (
                <div className="flex items-center justify-between p-6 border-b border-gray-200 rounded-t-lg flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
                    {locationCountry && (
                      <div
                        className="relative"
                        onMouseEnter={() => setIsFilterInfoHovered(true)}
                        onMouseLeave={() => setIsFilterInfoHovered(false)}
                      >
                        <svg className="w-4 h-4 text-gray-400 cursor-pointer" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {isFilterInfoHovered && (
                          <div
                            className="absolute left-0 mt-2 px-4 py-3 rounded-xl shadow-xl text-sm font-medium leading-relaxed z-50"
                            style={{ backgroundColor: '#222222', color: '#ffffff', width: '260px' }}
                          >
                            Only showing filters relevant to funding in your area.
                            <div
                              className="absolute -top-1.5 left-2 w-3 h-3 rotate-45"
                              style={{ backgroundColor: '#222222' }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setIsOpen(false); setActiveDropdown(null); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* sheetMode: Sort + Group buttons at top */}
              {sheetMode && (
                <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-100">
                  {/* Sort */}
                  <div className="relative">
                    <button
                      onClick={() => setIsSortOpen(!isSortOpen)}
                      className="inline-flex items-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
                      style={{ backgroundColor: '#fff', borderColor: '#000', color: '#000' }}
                    >
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                      </svg>
                      <span className="ml-2">{getCurrentSortLabel()}</span>
                      <svg className={`h-4 w-4 ml-1 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isSortOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsSortOpen(false)} />
                        <div className="absolute left-0 mt-2 w-64 rounded-lg shadow-lg z-50 border-2 bg-white" style={{ borderColor: '#615DEC' }}>
                          <div className="py-2">
                            <div className="px-4 py-2 text-xs font-semibold" style={{ color: '#7C7B7B', backgroundColor: '#F6F5EC' }}>SORT OPTIONS</div>
                            {getSortOptions().map((option) => (
                              <button key={option.value} onClick={() => handleSortChange(option.value)}
                                className="w-full text-left px-4 py-3 text-sm font-medium hover:opacity-80"
                                style={{ backgroundColor: (filters.ordering || '') === option.value ? '#F6F5EC' : 'transparent', color: (filters.ordering || '') === option.value ? '#615DEC' : '#222222' }}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{option.label}</span>
                                  {(filters.ordering || '') === option.value && (
                                    <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Group */}
                  <div className="relative">
                    <button
                      onClick={() => setIsGroupOpen(!isGroupOpen)}
                      className="inline-flex items-center px-3 py-2 min-h-[44px] border-2 rounded-md text-sm font-semibold transition-all focus:outline-none"
                      style={{ backgroundColor: filters.group_by ? '#000' : '#fff', borderColor: '#000', color: filters.group_by ? '#fff' : '#000' }}
                    >
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      <span className="ml-2">{filters.group_by ? `Grouped: ${getGroupLabel(filters.group_by)}` : 'Group'}</span>
                      <svg className={`h-4 w-4 ml-1 transition-transform ${isGroupOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isGroupOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsGroupOpen(false)} />
                        <div className="absolute left-0 mt-2 w-64 rounded-lg shadow-lg z-50 border-2 bg-white" style={{ borderColor: '#615DEC' }}>
                          <div className="py-2">
                            <div className="px-4 py-2 text-xs font-semibold" style={{ color: '#7C7B7B', backgroundColor: '#F6F5EC' }}>GROUP BY</div>
                            <button onClick={() => { posthog.capture('funding_filtered', { filter_type: 'group_by', filter_value: 'none' }); onFilterChange({ group_by: '' }); setIsGroupOpen(false); }}
                              className="w-full text-left px-4 py-3 text-sm font-medium hover:opacity-80"
                              style={{ backgroundColor: !filters.group_by ? '#F6F5EC' : 'transparent', color: !filters.group_by ? '#615DEC' : '#222222' }}
                            >
                              <div className="flex items-center justify-between"><span>None</span>
                                {!filters.group_by && <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>}
                              </div>
                            </button>
                            {groupOptions.map((option) => (
                              <button key={option.value} onClick={() => { posthog.capture('funding_filtered', { filter_type: 'group_by', filter_value: option.value }); onFilterChange({ group_by: option.value }); setIsGroupOpen(false); }}
                                className="w-full text-left px-4 py-3 text-sm font-medium hover:opacity-80"
                                style={{ backgroundColor: filters.group_by === option.value ? '#F6F5EC' : 'transparent', color: filters.group_by === option.value ? '#615DEC' : '#222222' }}
                              >
                                <div className="flex items-center justify-between"><span>{option.label}</span>
                                  {filters.group_by === option.value && <svg className="h-4 w-4" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Filter grid */}
              <div className={sheetMode ? 'pt-2 pb-3' : 'px-6 pt-5 pb-3 flex-shrink-0'} ref={gridRef}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filterConfigs.map(({ key, label }) => {
                    const selected = filters[key] || [];
                    const isActive = activeDropdown === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        ref={el => { buttonRefs.current[key] = el; }}
                        onClick={() => handleFilterButtonClick(key)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-md text-sm font-medium text-gray-700 text-left flex items-center justify-between bg-gray-100 hover:bg-gray-200 focus:outline-none transition-colors"
                      >
                        <span className="truncate mr-2">{label}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {selected.length > 0 && (
                            <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full font-medium leading-none">
                              {selected.length}
                            </span>
                          )}
                          <svg className={`h-4 w-4 text-gray-500 transition-transform ${isActive ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Fixed dropdown panel + backdrop */}
                {activeDropdown && (() => {
                  const config = filterConfigs.find(c => c.key === activeDropdown);
                  if (!config) return null;
                  const selected = filters[activeDropdown] || [];
                  const multiCol = config.options.length > 5;
                  return (
                    <>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 9998 }}
                        onClick={() => setActiveDropdown(null)}
                      />
                      <div
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-md overflow-y-auto"
                        style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, maxWidth: 'calc(100vw - 16px)', maxHeight: 320, zIndex: 9999 }}
                      >
                        {config.loading ? (
                          <div className="flex justify-center py-4"><LoadingSpinner size="small" /></div>
                        ) : config.grouped ? (
                          <div className="p-2">
                            {Object.entries(
                              config.options.reduce((groups, opt) => {
                                const g = opt.group || 'Other';
                                (groups[g] = groups[g] || []).push(opt);
                                return groups;
                              }, {})
                            ).map(([groupName, groupOptions], i, arr) => (
                              <div key={groupName} className={i < arr.length - 1 ? 'mb-1' : ''}>
                                <div className="px-2 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 mb-1">{groupName}</div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-1 gap-y-0">
                                  {groupOptions.map((option) => (
                                    <label key={option.value} className={`flex items-center cursor-pointer px-2 py-1.5 rounded ${option.unavailable ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                      <input
                                        type="checkbox"
                                        checked={selected.includes(option.value)}
                                        onChange={(e) => handleArrayFilterChange(activeDropdown, option.value, e.target.checked)}
                                        className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded flex-shrink-0"
                                      />
                                      <span className={`ml-2 text-sm leading-tight ${option.unavailable ? 'text-amber-700' : 'text-gray-700'}`}>{option.label}</span>
                                      {option.unavailable && (
                                        <svg className="w-3.5 h-3.5 text-amber-500 ml-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={`p-3 ${multiCol ? 'grid grid-cols-2 sm:grid-cols-3 gap-x-1 gap-y-0' : 'flex flex-col'}`}>
                            {config.options.map((option) => (
                              <label key={option.value} className={`flex items-center cursor-pointer px-2 py-1.5 rounded ${option.unavailable ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={selected.includes(option.value)}
                                  onChange={(e) => handleArrayFilterChange(activeDropdown, option.value, e.target.checked)}
                                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded flex-shrink-0"
                                />
                                <span className={`ml-2 text-sm leading-tight ${option.unavailable ? 'text-amber-700' : 'text-gray-700'}`}>{option.label}</span>
                                {option.unavailable && (
                                  <svg className="w-3.5 h-3.5 text-amber-500 ml-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Scrollable content — protected land only */}
              <div className={sheetMode ? 'py-4' : 'px-6 py-4 overflow-y-auto flex-1'}>
                <div className="space-y-6">
                  {/* Protected Land */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Protected Land
                    </label>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCategory('_protected_land')}
                        className="w-full px-4 py-3 text-left flex items-center justify-between bg-gray-100 hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-700">Protected Land Types</span>
                        <div className="flex items-center space-x-2">
                          {(filters.protected_land || []).length > 0 && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              {(filters.protected_land || []).length}
                            </span>
                          )}
                          <svg
                            className={`h-4 w-4 text-gray-500 transition-transform ${expandedCategories['_protected_land'] ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                    {expandedCategories['_protected_land'] && (
                      <div className="px-4 py-3 bg-white space-y-1">
                        
                        {/* Selected land types - float to top */}
                        {(filters.protected_land || []).length > 0 && (
                          <>
                            {PROTECTED_LAND_TYPES.filter(type => (filters.protected_land || []).includes(type.value)).map((type) => (
                              <label key={type.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={(e) => handleProtectedLandChange(type.value, e.target.checked)}
                                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-900">{type.label}</span>
                              </label>
                            ))}

                            {/* Threshold toggle as divider */}
                            <div className="py-2">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="flex-1 h-px bg-gray-200" />
                                <span className="text-xs text-gray-400 font-medium whitespace-nowrap">eligibility threshold</span>
                                <div className="flex-1 h-px bg-gray-200" />
                              </div>
                              <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs font-medium">
                                <button
                                  type="button"
                                  onClick={() => handleThresholdChange('eligible')}
                                  className="flex-1 py-2 transition-colors"
                                  style={{
                                    backgroundColor: (filters.protected_land_threshold || 'eligible') === 'eligible' ? '#dcfce7' : '#fff',
                                    color: (filters.protected_land_threshold || 'eligible') === 'eligible' ? '#166534' : '#222',
                                  }}
                                >
                                  Always Eligible
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleThresholdChange('include_consent')}
                                  className="flex-1 py-2 transition-colors"
                                  style={{
                                    backgroundColor: filters.protected_land_threshold === 'include_consent' ? '#ffedd5' : '#fff',
                                    color: filters.protected_land_threshold === 'include_consent' ? '#9a3412' : '#222',
                                  }}
                                >
                                  Consent Required
                                </button>
                              </div>
                              <div className="mt-2 h-px bg-gray-200" />
                            </div>
                          </>
                        )}

                        {/* Unselected land types */}
                        {PROTECTED_LAND_TYPES.filter(type => !(filters.protected_land || []).includes(type.value)).map((type) => (
                          <label key={type.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={false}
                              onChange={(e) => handleProtectedLandChange(type.value, e.target.checked)}
                              className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                          </label>
                        ))}
                      </div>
                    )}  
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer — desktop modal only */}
              {!sheetMode && (
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
                  <button
                    onClick={onClearFilters}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear all
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setIsOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        if (typeof onShowResults === 'function') onShowResults();
                      }}
                      className="px-6 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Show results
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {(hasActiveFilters || hasActiveSearch) && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex flex-wrap gap-2">
            {/* Search badge — always first */}
            {hasActiveSearch && searchQuery && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-950">
                <span className="text-indigo-300">Search:</span>
                <span className="font-semibold text-white ml-1">"{searchQuery}"</span>
                <button
                  type="button"
                  onClick={onClearSearch}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-300 hover:text-white hover:bg-indigo-700"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                    <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )}
            {filters.search && (
              <span 
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: 'rgba(97, 93, 236, 0.1)',
                  color: '#615DEC'
                }}
              >
                Search: "{filters.search}"
                <button
                  type="button"
                  onClick={() => onFilterChange({ search: '' })}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full"
                  style={{
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(97, 93, 236, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                    <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )}
            {/* Fund Collection filter badges */}
            {(filters.fund_collection || []).map(collectionId => {
              const collection = fundCollections.find(c => c.id === collectionId);
              return (
                <span key={collectionId} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${collection?.unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-pink-100'}`}>
                  {collection?.unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={collection?.unavailable ? 'text-amber-600' : 'text-pink-600'}>Collection:</span>
                  <span className={`font-semibold ml-1 ${collection?.unavailable ? 'text-amber-800' : 'text-pink-800'}`}>{collection?.name || 'Not specified'}</span>
                  <button type="button" onClick={() => handleArrayFilterChange('fund_collection', collectionId, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${collection?.unavailable ? 'hover:bg-amber-200' : 'hover:bg-pink-200'}`}>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}

            {/* Funder filter badges */}
            {(filters.funders || []).map(funderId => {
              const funder = funders.find(f => f.id === funderId);
              return (
                <span key={funderId} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${funder?.unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-orange-100'}`}>
                  {funder?.unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={funder?.unavailable ? 'text-amber-600' : 'text-orange-600'}>Funder:</span>
                  <span className={`font-semibold ml-1 ${funder?.unavailable ? 'text-amber-800' : 'text-orange-800'}`}>
                    {funder?.name || 'Not specified'}
                  </span>
                  <button type="button" onClick={() => handleArrayFilterChange('funders', funderId, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${funder?.unavailable ? 'hover:bg-amber-200' : 'hover:bg-orange-200'}`}>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            
            {(filters.aim || []).map(val => {
              const unavailable = isFilterValueUnavailable('aim', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-lime-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-lime-600'}>Aim:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-lime-800'}`}>{FUNDING_AIMS.find(a => a.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('aim', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-lime-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.interests || []).map(val => {
              const unavailable = isFilterValueUnavailable('interests', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-teal-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-teal-600'}>Interest:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-teal-800'}`}>{INTERESTS.find(i => i.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('interests', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-teal-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}

            {(filters.farming_type || []).map(val => {
              const unavailable = isFilterValueUnavailable('farming_type', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-green-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-green-600'}>Farm type:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-green-800'}`}>{FARMING_TYPES.find(t => t.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('farming_type', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-green-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.status || []).map(val => {
              const unavailable = isFilterValueUnavailable('status', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-blue-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-blue-600'}>Status:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-blue-800'}`}>{STATUS_OPTIONS.find(s => s.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('status', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-blue-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.competitive || []).map(val => {
              const unavailable = isFilterValueUnavailable('competitive', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-amber-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-amber-600'}>Competitive:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-amber-800'}`}>{COMPETITIVE_STATUS.find(c => c.value === val)?.label || val}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('competitive', val, false)}
                    className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-amber-200"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.funding_type || []).map(val => {
              const unavailable = isFilterValueUnavailable('funding_type', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-yellow-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-yellow-600'}>Funding type:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-yellow-800'}`}>{FUNDING_TYPES.find(t => t.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('funding_type', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-yellow-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.land_types || []).map(val => {
              const unavailable = isFilterValueUnavailable('land_types', val);
              return (
                <span key={val} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${unavailable ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-teal-100'}`}>
                  {unavailable && (
                    <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className={unavailable ? 'text-amber-600' : 'text-teal-600'}>Land type:</span>
                  <span className={`font-semibold ml-1 ${unavailable ? 'text-amber-800' : 'text-teal-800'}`}>{LAND_TYPES.find(t => t.value === val)?.label}</span>
                  <button
                    type="button"
                    onClick={() => handleArrayFilterChange('land_types', val, false)}
                    className={`ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full ${unavailable ? 'hover:bg-amber-200' : 'hover:bg-teal-200'}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                      <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {(filters.protected_land || []).map((landType) => (
              <span
                key={landType}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm"
                style={{
                  backgroundColor: filters.protected_land_threshold === 'include_consent' ? '#ffedd5' : '#dcfce7',
                  color: filters.protected_land_threshold === 'include_consent' ? '#9a3412' : '#166534',
                }}
              >
                <span style={{ opacity: 0.75 }}>Protected land:</span>
                <span className="font-semibold ml-1">{landType}</span>
                <button
                  type="button"
                  onClick={() => handleProtectedLandChange(landType, false)}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full"
                  style={{ color: filters.protected_land_threshold === 'include_consent' ? '#9a3412' : '#166534' }}
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                    <path d="M1.5 1.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Results count — below filters */}
      <p className="text-sm font-semibold mb-4" style={{ color: '#222222' }}>
        {currentResultsLength > 0 ? (
          filters.group_by
            ? <>{resultsCount} opportunities in {groupCount} {groupCount === 1 ? 'group' : 'groups'}</>
            : <>Showing {currentResultsLength} of {resultsCount} opportunities</>
        ) : (
          'No opportunities found'
        )}
      </p>
    </>
  );
};

export default FundingFilters;